import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { cn } from "../lib/utils"
import { resolveIcon } from "./resolve-icon"
import { ICON_SIZE, resolveColor } from "../lib/style-resolver"

export function IconRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const atomDefaults = useAtomDefaults("icon")
  const resolvedVariant = (element as { variant?: string }).variant
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = typeof (element as { size?: unknown }).size === "string"
    ? String((element as { size?: string }).size)
    : atomDefaults.defaultSize ?? "default"

  const rawName = element.name ?? element.icon ?? ""
  const name = rawName.includes("#{")
    ? String(resolveSymbol(rawName) ?? "")
    : rawName

  const IconComponent = resolveIcon(name)
  if (!IconComponent) return null

  // Resolve color via centralized resolver
  const rawColor = element.color as string | undefined
  const resolvedColor = rawColor?.includes("#{")
    ? String(resolveSymbol(rawColor) ?? "")
    : rawColor
  const { className: colorClass, style: colorStyle } = resolveColor(resolvedColor)

  // Resolve size via centralized tokens
  const rawSize = element.size
  const inlineStyle: React.CSSProperties = { ...colorStyle }
  let sizeClass = ""
  if (typeof rawSize === "number") {
    inlineStyle.width = rawSize
    inlineStyle.height = rawSize
  } else {
    sizeClass = ICON_SIZE[rawSize ?? "default"] ?? ICON_SIZE.default
  }

  const hasStyle = Object.keys(inlineStyle).length > 0
  const shouldFill = element.fill === true

  return (
    <IconComponent
      className={cn(sizeClass, colorClass, element.className)}
      style={hasStyle ? inlineStyle : undefined}
      fill={shouldFill ? "currentColor" : "none"}
      data-testid={`icon-${name}`}
      data-atom-type="icon"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
    />
  )
}
